import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  format, startOfMonth, endOfMonth, startOfWeek, addDays, isSameMonth,
  addMonths, subMonths, isToday
} from "date-fns";

export default function CheckInCalendarModal({ open, onOpenChange, checkedDates }) {
  const [viewDate, setViewDate] = useState(new Date());

  const monthStart = startOfMonth(viewDate);
  const monthEnd = endOfMonth(viewDate);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });

  // Build 6 weeks of days
  const weeks = [];
  let day = calStart;
  while (day <= monthEnd || weeks.length < 6) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(day));
      day = addDays(day, 1);
    }
    weeks.push(week);
    if (day > monthEnd && weeks.length >= 4) break;
  }

  const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-[hsl(220,20%,10%)] border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="font-display text-white">Check-In History</DialogTitle>
        </DialogHeader>

        {/* Month navigation */}
        <div className="flex items-center justify-between mb-3">
          <Button variant="ghost" size="icon" onClick={() => setViewDate(subMonths(viewDate, 1))}
            className="text-white/60 hover:text-white hover:bg-white/10">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="font-semibold text-sm">{format(viewDate, "MMMM yyyy")}</span>
          <Button variant="ghost" size="icon" onClick={() => setViewDate(addMonths(viewDate, 1))}
            className="text-white/60 hover:text-white hover:bg-white/10">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Day labels */}
        <div className="grid grid-cols-7 mb-1">
          {DAY_LABELS.map((d) => (
            <div key={d} className="text-center text-[10px] font-medium text-white/30 py-1">{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="space-y-1">
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 gap-1">
              {week.map((d, di) => {
                const dateStr = format(d, "yyyy-MM-dd");
                const checked = checkedDates.has(dateStr);
                const inMonth = isSameMonth(d, viewDate);
                const today = isToday(d);
                return (
                  <div key={di} className="flex items-center justify-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all
                      ${!inMonth ? "opacity-20" : ""}
                      ${checked ? "bg-emerald-500 text-white" : today ? "ring-2 ring-primary text-primary" : "text-white/60"}
                    `}>
                      {checked ? "✓" : format(d, "d")}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/10 text-xs text-white/40">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-emerald-500" />
            <span>Checked in</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full ring-2 ring-primary" />
            <span>Today</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}